import React, { useCallback, useMemo, useState } from "react";
import {
    useCurrentOrganizationMembers,
    useOrganizationClientUsers,
} from "@api/organizationMember.api";
import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import {
    SERIES_FILTER_ICONS,
    SERIES_FILTER_LABELS,
    toListedSeries,
    toSeriesFilterValue,
} from "@components/activityTimeline/activityTimelineSeriesFilter";
import type { ActivityFilterRows } from "@components/activityTimeline/filters/activityFilters.types";
import { useOrganizationActivityPeopleById } from "../OrganizationActivityPeopleProvider";
import type { OrganizationActivityPerson } from "../useOrganizationActivityPeople";
import {
    type PersonItem,
    toMatchingPickerItems,
    toPersonItem,
} from "./organizationActorPeople";

/** A picked row: a person, or a series with its icon. */
interface SelectedActor {
    key: string;
    name: string;
    avatar?: React.ReactNode;
}

/** Updated by: members, plus timeline series picks. */
export interface OrganizationActorSelection {
    actorIds?: number[];
    series?: ActivitySeries[];
}

export const CLEARED_ORGANIZATION_ACTOR_SELECTION = {
    actorIds: undefined,
    series: undefined,
} satisfies OrganizationActorSelection;

interface OrganizationActorFilterComboboxProps extends OrganizationActorSelection {
    /** Series rows listed above the members; none and no client users keeps the plain members list. */
    pickableSeries: readonly ActivitySeries[];
    /** The page's Clients filter; narrows the listed client users. */
    clientWorkspaceIds?: string[];
    onChange: (selection: OrganizationActorSelection) => void;
}

const toSeriesActor = (series: ActivitySeries): SelectedActor => ({
    key: toSeriesFilterValue(series),
    name: SERIES_FILTER_LABELS[series],
    avatar: SERIES_FILTER_ICONS[series],
});

const matchesPrefilteredQuery = () => true;

/** The firm Updated by: series, Members, then the clients' users. */
export function useOrganizationActorFilterRows({
    actorIds,
    series,
    pickableSeries,
    clientWorkspaceIds,
    onChange,
}: OrganizationActorFilterComboboxProps): ActivityFilterRows<string> {
    const [search, setSearch] = useState("");
    const { data: members = [] } = useCurrentOrganizationMembers();
    const { data: listedClientUsers = [] } = useOrganizationClientUsers({
        clientWorkspaceIds,
    });
    const peopleById = useOrganizationActivityPeopleById();

    const lowerSearch = search.toLowerCase();

    const listedSeries = useMemo(
        () => toListedSeries({ pickableSeries, pickedSeries: series }),
        [series, pickableSeries],
    );

    const memberItems = useMemo(
        () => members.map(({ user }) => toPersonItem(user)),
        [members],
    );
    const clientUserItems = useMemo(
        () => listedClientUsers.map(toPersonItem),
        [listedClientUsers],
    );

    const pickablePeopleById = useMemo(
        () =>
            new Map([
                ...peopleById,
                ...[...memberItems, ...clientUserItems].map(
                    (item): [number, OrganizationActivityPerson] => [
                        item.id,
                        item.user,
                    ],
                ),
            ]),
        [peopleById, memberItems, clientUserItems],
    );

    const filteredMemberItems = useMemo(
        () => toMatchingPickerItems({ items: memberItems, lowerSearch }),
        [memberItems, lowerSearch],
    );
    const filteredClientUserItems = useMemo(
        () => toMatchingPickerItems({ items: clientUserItems, lowerSearch }),
        [clientUserItems, lowerSearch],
    );

    const hasSections = listedSeries.length > 0 || clientUserItems.length > 0;

    const sections = useMemo<Array<PickerSectionData<string>>>(() => {
        const seriesItems = listedSeries
            .map(toSeriesActor)
            .filter(({ name }) => name.toLowerCase().includes(lowerSearch))
            .map(({ key, name, avatar }) => ({
                value: key,
                label: name,
                leading: avatar,
            }));
        return [
            { key: "agents", items: seriesItems },
            {
                key: "members",
                title: "Members",
                selectAll: true,
                items: filteredMemberItems,
            },
            ...(filteredClientUserItems.length > 0
                ? [
                      {
                          key: "client-users",
                          title: "Client users",
                          selectAll: true,
                          items: filteredClientUserItems,
                      },
                  ]
                : []),
        ];
    }, [
        listedSeries,
        filteredMemberItems,
        filteredClientUserItems,
        lowerSearch,
    ]);

    const selectedItems = useMemo<PersonItem[]>(
        () =>
            (actorIds ?? []).flatMap((id) => {
                const person = pickablePeopleById.get(id);
                return person ? [toPersonItem(person)] : [];
            }),
        [actorIds, pickablePeopleById],
    );

    const selectedActors = useMemo<SelectedActor[]>(
        () => [
            ...listedSeries
                .filter((listed) => series?.includes(listed))
                .map(toSeriesActor),
            ...selectedItems.map((item) => ({
                key: String(item.id),
                name: item.name,
            })),
        ],
        [listedSeries, series, selectedItems],
    );

    const selectedCount = selectedActors.length;

    const triggerAppendLabel = useMemo(() => {
        if (selectedCount === 0) {
            return "Updated by";
        }
        if (selectedCount <= 2) {
            return selectedActors.map((actor) => actor.name).join(", ");
        }
        return `Updated by ${selectedCount}`;
    }, [selectedActors, selectedCount]);

    const applySelection = useCallback(
        (values: string[]) => {
            const ids = values.flatMap((value) =>
                pickablePeopleById.has(Number(value)) ? [Number(value)] : [],
            );
            const pickedSeries = listedSeries.filter((listed) =>
                values.includes(toSeriesFilterValue(listed)),
            );
            onChange({
                actorIds: ids.length > 0 ? ids : undefined,
                series: pickedSeries.length > 0 ? pickedSeries : undefined,
            });
        },
        [pickablePeopleById, listedSeries, onChange],
    );

    const resetFilter = useCallback(() => {
        onChange(CLEARED_ORGANIZATION_ACTOR_SELECTION);
    }, [onChange]);

    return {
        list: hasSections ? { sections } : { items: filteredMemberItems },
        value: selectedActors.map((actor) => actor.key),
        onValueChange: applySelection,
        onClear: resetFilter,
        chipLabel: selectedCount > 0 ? triggerAppendLabel : undefined,
        filter: matchesPrefilteredQuery,
        onInputValueChange: setSearch,
    };
}
